'use client';
import { useState } from 'react';
import { useResource } from '@/lib/use-resource';

// Renders the weekly code-health audit produced by
// scripts/system/code-health-audit.sh on Saturday at 23:37.
//
// Deltas vs prior week appear inline (↓ green = improved, ↑ orange = drifted).
// Each metric's file list is collapsed by default; click the header to drill in.

interface HealthBuild {
  tscOk: boolean;
  tscErrors: number;
  lintOk: boolean;
  lintWarnings: number;
  testsOk: boolean;
  testsPass: number;
  testsFail: number;
  auditHighCrit: number;
}

interface FileRow { path: string; lines: number }

interface HealthMetrics {
  godFiles: FileRow[];
  fatRoutes: FileRow[];
  adHocFetches: string[];
  datedComments: string[];
  deadWorktrees: string[];
  staleBaks: string[];
  untrackedJunk: string[];
}

interface HealthDelta {
  outstanding: number;
  fixed: number | null;        // items removed since last week (was prior, not current)
  newlyAdded: number | null;   // items that appeared this week
  unchanged: number | null;    // items in both runs (no progress)
  fixedItems?: string[];       // optional — older payloads predate the set-diff
  newlyAddedItems?: string[];
}

interface HealthPayload {
  asOf: string | null;
  gitSha?: string;
  build?: HealthBuild;
  metrics?: HealthMetrics;
  deltas?: Record<string, HealthDelta>;
  priorAsOf?: string | null;
  message?: string;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  const day = d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return `${day} · ${time}`;
}

function nextSaturday(fromIso: string): string {
  const d = new Date(fromIso);
  const daysToSat = (6 - d.getDay() + 7) % 7 || 7;
  const next = new Date(d);
  next.setDate(d.getDate() + daysToSat);
  return next.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

function StatusPill({ ok, label, value }: { ok: boolean; label: string; value: string }) {
  const cls = ok
    ? 'border-emerald-500/40 text-emerald-300 bg-emerald-500/5'
    : 'border-red-500/40 text-red-300 bg-red-500/5';
  return (
    <span className={`inline-flex items-baseline gap-2 px-3 py-1.5 rounded border ${cls}`}>
      <span className="text-[10px] uppercase tracking-wider font-semibold">{label}</span>
      <span className="text-[12px] font-mono tabular-nums">{value}</span>
    </span>
  );
}

// 5-column grid: title | outstanding | fixed | unchanged | details-toggle
// Same template applies to header row + every metric row so columns line up.
const ROW_GRID = 'grid grid-cols-[minmax(0,1fr)_5rem_5rem_5rem_4.5rem] gap-x-3 items-baseline';

function MetricColumnHeaders() {
  return (
    <div className={`${ROW_GRID} pb-2 border-b border-[#1e293b]/60 text-[10px] uppercase tracking-wider text-[#475569] font-semibold`}>
      <div></div>
      <div className="text-right">Outstanding</div>
      <div className="text-right">Fixed</div>
      <div className="text-right">Unchanged</div>
      <div></div>
    </div>
  );
}

function NumberCell({ value, accent }: { value: number | null; accent: 'amber' | 'emerald' | 'slate' | 'muted' }) {
  if (value == null) {
    return <div className="text-right text-[10px] text-[#475569] font-mono">—</div>;
  }
  const colorClass = value === 0
    ? 'text-[#64748b]'
    : accent === 'amber' ? 'text-amber-200'
    : accent === 'emerald' ? 'text-emerald-300'
    : accent === 'slate' ? 'text-[#cbd5e1]'
    : 'text-[#64748b]';
  return <div className={`text-right text-[12px] font-mono tabular-nums ${colorClass}`}>{value}</div>;
}

interface MetricSectionProps {
  title: string;
  threshold?: string;
  delta?: HealthDelta;
  rows: FileRow[] | string[];
  defaultOpen?: boolean;
}

function MetricSection({ title, threshold, delta, rows, defaultOpen = false }: MetricSectionProps) {
  const [open, setOpen] = useState(defaultOpen);
  const hasRows = rows.length > 0;
  const isFileRow = hasRows && typeof rows[0] === 'object';
  const fixedItems = delta?.fixedItems ?? [];
  const newlyAddedItems = delta?.newlyAddedItems ?? [];
  const hasFixedItems = fixedItems.length > 0;
  const hasNewlyAddedItems = newlyAddedItems.length > 0;
  const expandable = hasRows || hasFixedItems || hasNewlyAddedItems;

  return (
    <div className="border-b border-[#1e293b]/60 py-2.5">
      <button
        type="button"
        disabled={!expandable}
        onClick={() => setOpen((o) => !o)}
        className={`w-full ${ROW_GRID} text-left ${expandable ? 'cursor-pointer hover:opacity-90' : 'cursor-default'}`}
      >
        <div className="min-w-0">
          <span className="text-[11px] uppercase tracking-wider text-[#cbd5e1] font-semibold mr-2">{title}</span>
          {threshold && <span className="text-[10px] text-[#64748b]">{threshold}</span>}
        </div>
        <NumberCell value={delta?.outstanding ?? null} accent="amber" />
        <NumberCell value={delta?.fixed ?? null} accent="emerald" />
        <NumberCell value={delta?.unchanged ?? null} accent="slate" />
        <div className="text-right text-[10px] uppercase tracking-wide text-[#64748b]">
          {expandable ? (open ? 'hide' : 'details') : ''}
        </div>
      </button>
      {open && (
        <div className="mt-3 ml-1 space-y-3">
          {hasFixedItems && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-emerald-300 mb-1">Fixed since last week ({fixedItems.length})</div>
              {fixedItems.map((p) => (
                <div key={p} className="text-[11px] font-mono text-emerald-300/80 line-through">{p}</div>
              ))}
            </div>
          )}
          {hasNewlyAddedItems && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-amber-300 mb-1">New this week ({newlyAddedItems.length})</div>
              {newlyAddedItems.map((p) => (
                <div key={p} className="text-[11px] font-mono text-amber-200">{p}</div>
              ))}
            </div>
          )}
          {/* All current outstanding */}
          {hasRows && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[#94a3b8] mb-1">All outstanding ({rows.length})</div>
              {isFileRow
                ? (rows as FileRow[]).map((r) => (
                    <div key={r.path} className="flex items-baseline justify-between text-[11px] font-mono text-[#94a3b8]">
                      <span>{r.path}</span>
                      <span className="text-[#64748b] tabular-nums">{r.lines}</span>
                    </div>
                  ))
                : (rows as string[]).map((p) => (
                    <div key={p} className="text-[11px] font-mono text-[#94a3b8]">{p}</div>
                  ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function HealthTab() {
  const { data, loading } = useResource<HealthPayload>('/api/code-health');

  if (loading) return <div className="text-[12px] text-[#64748b] py-8">Loading…</div>;
  if (!data || !data.asOf || !data.build || !data.metrics) {
    return (
      <div className="bg-[#0d1117] border border-[#1e293b] rounded-lg p-6 text-[#cbd5e1] text-[12px] leading-relaxed">
        <div className="text-[13px] mb-2">No audit yet.</div>
        <div className="text-[#64748b]">
          {data?.message ?? 'The Saturday 23:37 cron writes this surface. To populate now, run:'}
          <pre className="mt-2 px-3 py-2 bg-[#0a0e14] border border-[#1e293b] rounded text-[11px] text-amber-200 inline-block">
            bash scripts/system/code-health-audit.sh
          </pre>
        </div>
      </div>
    );
  }

  const { build, metrics, deltas } = data;

  return (
    <div className="space-y-4">
      <div className="bg-[#0d1117] border border-[#1e293b] rounded-lg p-5">
        <div className="flex items-baseline justify-between flex-wrap gap-2 mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-[#94a3b8]">Build & Tests</div>
            <div className="text-[11px] text-[#64748b] mt-0.5">
              git <span className="font-mono">{data.gitSha}</span>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <StatusPill ok={build.testsOk} label="Tests" value={`${build.testsPass}/${build.testsPass + build.testsFail}`} />
            <StatusPill ok={build.tscOk} label="TSC" value={build.tscOk ? '✓' : `${build.tscErrors} err`} />
            <StatusPill ok={build.lintWarnings === 0} label="Lint" value={`${build.lintWarnings}w`} />
            <StatusPill ok={build.auditHighCrit === 0} label="NPM Audit" value={`${build.auditHighCrit} h+c`} />
          </div>
        </div>

        <MetricColumnHeaders />
        <MetricSection title="God files" threshold="(>500 lines)" delta={deltas?.godFiles} rows={metrics.godFiles} />
        <MetricSection title="Fat API routes" threshold="(>200 lines)" delta={deltas?.fatRoutes} rows={metrics.fatRoutes} />
        <MetricSection title="Ad-hoc fetches" threshold="(no useResource)" delta={deltas?.adHocFetches} rows={metrics.adHocFetches} />
        <MetricSection title="Dated changelog comments" threshold="(// 2026-XX-XX:)" delta={deltas?.datedComments} rows={metrics.datedComments} />
        <MetricSection title="Dead worktrees" delta={deltas?.deadWorktrees} rows={metrics.deadWorktrees} />
        <MetricSection title="Stale .bak files" delta={deltas?.staleBaks} rows={metrics.staleBaks} />
        {metrics.untrackedJunk.length > 0 && (
          <MetricSection title="Untracked log/bak/tmp files" delta={deltas?.untrackedJunk} rows={metrics.untrackedJunk} defaultOpen />
        )}
      </div>

      <div className="text-[10px] text-[#64748b] flex items-baseline justify-between font-mono">
        <span>last audit: {formatDate(data.asOf)}</span>
        <span>next audit: {nextSaturday(data.asOf)} · 23:37</span>
      </div>

      <div className="text-[11px] text-[#cbd5e1] italic text-center pt-2">
        Take ONE thing from this list to fix Sunday.
      </div>
    </div>
  );
}
